'use client'

import nookies from 'nookies'
import { AUTH_TOKEN_KEY } from '../../../utils/constants'

const ACLICK_ENDPOINT = 'https://t.o-s.io/aclick'
const REQUIRED_OFFSITE_AD_CLICK_PARAMS = [
  'atag',
  'asvt',
  'seller_id',
  'client_id',
]
const OPTIONAL_OFFSITE_AD_CLICK_PARAMS = [
  'gclid',
  'fbclid',
  'utm_source',
  'utm_campaign',
]
const RETRY_QUEUE_STORAGE_KEY = 'osmos_aclick_retry_queue'
const SENT_SESSION_PREFIX = 'osmos_aclick_sent_'
const ANONYMOUS_CLI_UBID_STORAGE_KEY = 'osmos_cli_ubid_anonymous'
const SEGMENT_USER_TRAITS_STORAGE_KEY = 'ajs_user_traits'
const LINKPASS_ID_TRAIT_KEY = 'linkpass id'

const getQueryValue = (query, key) => {
  const value = query?.[key]
  if (Array.isArray(value)) {
    return value[0]
  }
  return value
}

const buildDedupeKey = params =>
  REQUIRED_OFFSITE_AD_CLICK_PARAMS.map(key => params[key]).join('|')

const safeSessionStorage = {
  getItem(key) {
    try {
      return window.sessionStorage.getItem(key)
    } catch {
      return null
    }
  },
  setItem(key, value) {
    try {
      window.sessionStorage.setItem(key, value)
    } catch {
      // Ignore storage failures in private browsing.
    }
  },
}

const safeLocalStorage = {
  getItem(key) {
    try {
      return window.localStorage.getItem(key)
    } catch {
      return null
    }
  },
  setItem(key, value) {
    try {
      window.localStorage.setItem(key, value)
    } catch {
      // Ignore storage failures in private browsing.
    }
  },
}

const readRetryQueue = () => {
  const storedQueue = safeLocalStorage.getItem(RETRY_QUEUE_STORAGE_KEY)
  if (!storedQueue) {
    return []
  }

  try {
    const parsedQueue = JSON.parse(storedQueue)
    return Array.isArray(parsedQueue) ? parsedQueue : []
  } catch {
    return []
  }
}

const writeRetryQueue = queue => {
  if (!queue.length) {
    safeLocalStorage.setItem(RETRY_QUEUE_STORAGE_KEY, '[]')
    return
  }

  safeLocalStorage.setItem(RETRY_QUEUE_STORAGE_KEY, JSON.stringify(queue))
}

const hasAlreadySent = dedupeKey =>
  safeSessionStorage.getItem(`${SENT_SESSION_PREFIX}${dedupeKey}`) === '1'

const markAsSent = dedupeKey => {
  safeSessionStorage.setItem(`${SENT_SESSION_PREFIX}${dedupeKey}`, '1')
}

const getTrimmedString = value =>
  value != null && String(value).trim() ? String(value).trim() : null

const getLinkPassId = () => {
  const storedTraits = safeLocalStorage.getItem(SEGMENT_USER_TRAITS_STORAGE_KEY)
  if (!storedTraits) {
    return null
  }

  try {
    const traits = JSON.parse(storedTraits)
    return getTrimmedString(traits?.[LINKPASS_ID_TRAIT_KEY])
  } catch {
    return null
  }
}

const getOrCreateAnonymousUuid = () => {
  const existingId = getTrimmedString(
    safeLocalStorage.getItem(ANONYMOUS_CLI_UBID_STORAGE_KEY)
  )
  if (existingId) {
    return existingId
  }

  const uuid = crypto.randomUUID()
  safeLocalStorage.setItem(ANONYMOUS_CLI_UBID_STORAGE_KEY, uuid)
  return uuid
}

const resolveAnonymousCliUbid = () => {
  const linkPassId = getLinkPassId()
  if (linkPassId) {
    return linkPassId
  }

  const deviceId = getTrimmedString(window.__NEXT_DATA__?.props?.userKey)
  if (deviceId) {
    return deviceId
  }

  if (typeof crypto?.randomUUID === 'function') {
    return getOrCreateAnonymousUuid()
  }

  return null
}

const resolveCliUbid = () => {
  if (typeof window === 'undefined') {
    return null
  }

  const account = window.__NEXT_DATA__?.props?.account
  const { [AUTH_TOKEN_KEY]: authToken } = nookies.get() || {}

  if (account?.uid && authToken) {
    return String(account.uid).trim()
  }

  return resolveAnonymousCliUbid()
}

export const extractOffsiteAdClickParams = (query = {}) => {
  const params = {}

  for (const key of REQUIRED_OFFSITE_AD_CLICK_PARAMS) {
    const value = getQueryValue(query, key)
    if (typeof value !== 'string' || !value.trim()) {
      return null
    }
    params[key] = value.trim()
  }

  for (const key of OPTIONAL_OFFSITE_AD_CLICK_PARAMS) {
    const value = getQueryValue(query, key)
    if (typeof value === 'string' && value.trim()) {
      params[key] = value.trim()
    }
  }

  return params
}

export const buildOffsiteAdClickUrl = params => {
  const url = new URL(ACLICK_ENDPOINT)

  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') {
      url.searchParams.set(key, value)
    }
  })

  return url.toString()
}

export const sendOffsiteAdClick = async (
  params,
  { fetch: fetchImpl = fetch } = {}
) => {
  const response = await fetchImpl(buildOffsiteAdClickUrl(params), {
    method: 'GET',
    credentials: 'omit',
  })

  if (!response.ok) {
    throw new Error(`aclick failed: ${response.status}`)
  }
}

export const flushOffsiteAdClickRetryQueue = async (options = {}) => {
  const remainingQueue = []

  for (const payload of readRetryQueue()) {
    const dedupeKey = buildDedupeKey(payload)
    if (hasAlreadySent(dedupeKey)) {
      continue
    }

    try {
      await sendOffsiteAdClick(payload, options)
      markAsSent(dedupeKey)
    } catch {
      remainingQueue.push(payload)
    }
  }

  writeRetryQueue(remainingQueue)
}

export const executeOffsiteAdClickLanding = async (
  query = {},
  options = {}
) => {
  await flushOffsiteAdClickRetryQueue(options)

  const params = extractOffsiteAdClickParams(query)
  if (!params) {
    return
  }

  const dedupeKey = buildDedupeKey(params)
  if (hasAlreadySent(dedupeKey)) {
    return
  }

  const cliUbid = resolveCliUbid()
  if (!cliUbid) {
    return
  }

  const payload = {
    ...params,
    cli_ubid: cliUbid,
    event_time: String(Date.now()),
  }

  try {
    await sendOffsiteAdClick(payload, options)
    markAsSent(dedupeKey)
  } catch {
    const retryQueue = readRetryQueue()
    const isQueued = retryQueue.some(
      queuedPayload => buildDedupeKey(queuedPayload) === dedupeKey
    )

    if (!isQueued) {
      retryQueue.push(payload)
      writeRetryQueue(retryQueue)
    }
  }
}

export const registerOffsiteAdClickLanding = (query = {}, options = {}) => {
  if (typeof window === 'undefined') {
    return
  }

  const scheduleTask = callback => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => {
        callback()
      })
      return
    }

    setTimeout(callback, 0)
  }

  scheduleTask(() => {
    executeOffsiteAdClickLanding(query, options).catch(() => {})
  })
}
